import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { DineAwaitingBill } from 'api/types';

/**
 * "New bill": a bill is generated from an order, never keyed in. Pick a table
 * whose order is served and waiting. A row the kitchen has not finished is
 * shown but blocked (BR-022).
 */
interface Props {
  isOpen: boolean;
  awaiting: DineAwaitingBill[];
  onGenerate: (orderId: string) => void | Promise<void>;
  onClose: () => void;
}

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DineOpsNewBillModal: React.FC<Props> = ({ isOpen, awaiting, onGenerate, onClose }) => {
  const [picked, setPicked] = useState<string>('');
  if (!isOpen) return null;
  const sel = awaiting.find((a) => a.orderId === picked);

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">New bill</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          <p style={{ marginTop: 0, fontSize: 13, opacity: 0.8 }}>
            A bill is generated from an order, never keyed in. Pick a table whose order is served and waiting for a bill.
          </p>
          <div className="form-group">
            <label className="form-label">Order <span className="text-danger">*</span></label>
            <select className="form-input" value={picked} onChange={(e) => setPicked(e.target.value)}>
              <option value="">Select an order awaiting billing</option>
              {awaiting.map((a) => (
                <option key={a.orderId} value={a.orderId}>
                  {a.roomNumber && !a.tableNumber ? `Room ${a.roomNumber}` : a.tableNumber} · {a.orderNumber}{a.canBill ? '' : ' (kitchen not done)'}
                </option>
              ))}
            </select>
          </div>
          {sel && (
            <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
              <span style={{ opacity: 0.6 }}>Guest</span><span>{sel.guestPresence === 'IN_HOUSE' ? `In-house${sel.guestName ? ` · ${sel.guestName}` : ''}` : 'Walk-in'}{sel.isVip ? ' · VIP' : ''}</span>
              <span style={{ opacity: 0.6 }}>Covers</span><span>{sel.covers}</span>
              <span style={{ opacity: 0.6 }}>Waiting</span><span>{sel.billRequestedAt ? `${sel.waitingMinutes} min` : 'not asked yet (served)'}</span>
              <span style={{ opacity: 0.6 }}>Order total</span><span><b>{money(sel.subtotal)}</b> before tax</span>
              {!sel.canBill && (
                <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: '6px 10px', borderLeft: '3px solid var(--danger, #dc2626)', background: 'rgba(220,38,38,0.08)', fontSize: 12 }}>
                  {sel.unservedKots ? `${sel.unservedKots} kitchen ticket(s) are not served yet, so a bill cannot be generated (BR-022). Chase the kitchen or cancel the line.` : `${sel.unsentCount} line(s) are not sent to the kitchen. The waiter must send or remove them.`}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 10 }}>
            One bill for the whole order. Splitting into separate bills arrives in cycle 5.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!sel || !sel.canBill} onClick={() => sel && void onGenerate(sel.orderId)}>Generate bill</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsNewBillModal;
